/**
 * CategoryAddEditPageV2 — V2 edit-page chrome for Categories.
 * Same sticky `edit-header` recipe as the Sessions / Quick Taps
 * pages, swapping Save → form="category-form" type="submit" so
 * the page's button drives the form's onSubmit.
 *
 * The v1 page used a `ref.submit()` imperative call — V2 uses the
 * native `form` attribute pairing instead (matches Sessions /
 * Quick Taps). AddEditCategoryV2 still exposes `submit()` via ref
 * for callers that prefer the imperative path; we don't need it
 * here because the form's id + type="submit" button just work.
 */
import React, { useCallback, useState } from 'react';
import { useLocation, useParams, useSearchParams, Link } from 'react-router-dom';
import AddEditCategoryV2 from '../../AddEditCategory/v2/AddEditCategoryV2';
import { Button, cn } from '../../redesign';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function CategoryAddEditPageV2() {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = location.pathname.includes('edit');
  const backPath = searchParams.get('from') === 'audiobooks' ? '/audiobooks' : '/category';

  // The form fires the only GET /categories/{id} call and hands
  // back the title + updated_at via `onLoaded`. Memoised so the
  // form's load effect doesn't re-fire on every re-render here.
  const [categoryTitle, setCategoryTitle] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const handleFormLoaded = useCallback(({ category_title, category_updated_at }) => {
    setCategoryTitle(category_title || null);
    setLastSavedAt(category_updated_at || null);
  }, []);

  const lastSavedLabel = lastSavedAt
    ? `last saved ${new Date(lastSavedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : null;

  const eyebrow = isEdit
    ? (backPath === '/audiobooks' ? 'Editing audiobook category' : 'Editing category')
    : (backPath === '/audiobooks' ? 'New audiobook category' : 'New category');
  const title = isEdit
    ? (categoryTitle || `Category #${params.id}`)
    : 'Untitled category';

  return (
    <div className="bg-bg min-h-full">
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !categoryTitle && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div
              className="font-mono text-text-muted"
              style={{ fontSize: 12 }}
            >
              {isEdit ? (
                <>
                  category_id · <strong className="text-navy">{params.id}</strong>
                  {lastSavedLabel && (
                    <>
                      {' · '}
                      <span>{lastSavedLabel}</span>
                    </>
                  )}
                </>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to={backPath} className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            <Button
              variant="coral"
              type="submit"
              form="category-form"
              leadingIcon={SaveIconSvg}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <AddEditCategoryV2
          isEdit={isEdit}
          selectedId={params.id}
          onLoaded={handleFormLoaded}
        />
      </div>
    </div>
  );
}
